import React, { useEffect, useState } from "react";
import { config } from "../../config/environment";

interface HealthResponse {
  status: string;
  environment?: string;
  database?: string | null;
}

/**
 * Shows which backend environment and database the local frontend is connected to.
 * Only rendered when running in localhost mode (npm run local).
 */
const BackendEnvBanner: React.FC = () => {
  const [info, setInfo] = useState<{ environment: string; database: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config.IS_LOCALHOST || !config.BACKEND_URL) return;

    const url = `${config.BACKEND_URL.replace(/\/$/, "")}/health`;
    fetch(url, { credentials: "include" })
      .then((res) => res.json())
      .then((data: HealthResponse) => {
        setInfo({
          environment: data.environment ?? "unknown",
          database: data.database ?? null,
        });
      })
      .catch(() => setError("Backend unreachable"));
  }, []);

  if (!config.IS_LOCALHOST) return null;

  return (
    <div
      role="status"
      aria-label="Local backend info"
      style={{
        position: "fixed",
        bottom: 8,
        left: 8,
        zIndex: 9999,
        padding: "6px 10px",
        borderRadius: 6,
        background: "rgba(30, 41, 59, 0.95)",
        color: "#e2e8f0",
        fontSize: "11px",
        fontFamily: "ui-monospace, monospace",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {error ? (
        <span style={{ color: "#fca5a5" }}>{error}</span>
      ) : info ? (
        <>
          <span style={{ color: "#94a3b8" }}>Backend:</span>
          <span style={{ fontWeight: 600 }}>{info.environment}</span>
          {info.database && (
            <>
              <span style={{ color: "#64748b" }}>|</span>
              <span style={{ color: "#94a3b8" }}>DB:</span>
              <span>{info.database}</span>
            </>
          )}
        </>
      ) : (
        <span style={{ color: "#94a3b8" }}>Checking backend…</span>
      )}
    </div>
  );
};

export default BackendEnvBanner;
